import React, { useState, useEffect } from "react";
import Container from "@/components/marketing/Container";
import { ChevronDown } from "lucide-react";

/**
 * Shared legal document layout: sticky table of contents (left) + readable
 * single-column document (right). Light document body with dark site nav
 * and footer.
 *
 * Props:
 *  - title:        document H1
 *  - lastUpdated:  date string shown under the H1
 *  - sections:     [{ id, title, body }]  — body is a React node (JSX)
 *  - intro:        optional React node rendered before the first section
 */
export default function LegalPage({ title, lastUpdated, sections, intro }) {
  const [activeId, setActiveId] = useState(sections[0]?.id || "");
  const [tocOpen, setTocOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -75% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const onTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setActiveId(id);
    }
    setTocOpen(false);
  };

  return (
    <div className="bg-[#f7f7f9] pt-28 lg:pt-32 pb-24">
      <Container>
        {/* Document header */}
        <div className="mb-12 lg:mb-16 max-w-[720px]">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#14121c] mb-3">
            {title}
          </h1>
          <p className="text-[#6b6880] text-sm">Last updated: {lastUpdated}</p>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-16">
          {/* LEFT: sticky table of contents */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <button
              className="lg:hidden w-full flex items-center justify-between py-3 px-4 border border-[#e8e6ef] rounded-lg text-[#14121c] text-sm font-medium bg-white"
              onClick={() => setTocOpen(!tocOpen)}
              aria-expanded={tocOpen}
            >
              Table of Contents
              <ChevronDown
                className={`w-4 h-4 transition-transform ${tocOpen ? "rotate-180" : ""}`}
              />
            </button>

            <nav className={`${tocOpen ? "block mt-4" : "hidden"} lg:block`}>
              <p className="hidden lg:block font-mono text-xs uppercase tracking-widest text-[#6b6880] mb-4">
                Contents
              </p>
              <ul className="space-y-2.5 lg:max-h-[calc(100vh-14rem)] lg:overflow-y-auto lg:pr-2">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => onTocClick(e, s.id)}
                      className={`block text-[13.5px] leading-snug transition-colors ${
                        activeId === s.id
                          ? "text-[#7200CB] font-medium"
                          : "text-[#6b6880] hover:text-[#14121c]"
                      }`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* RIGHT: document content */}
          <div className="max-w-[720px]">
            {intro && (
              <div className="text-[#3a3646] text-[15.5px] leading-[1.75] [&_p]:mb-4 [&_p:last-child]:mb-0 mb-12">
                {intro}
              </div>
            )}
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-32">
                <h2
                  className={`font-heading text-[20px] font-semibold text-[#14121c] mb-4 ${
                    i === 0 ? "mt-0" : "mt-12"
                  }`}
                >
                  {s.title}
                </h2>
                <div className="text-[#3a3646] text-[15.5px] leading-[1.75] [&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1.5 [&_a]:text-[#7200CB] [&_a]:underline [&_strong]:text-[#14121c] [&_strong]:font-semibold [&_em]:italic">
                  {s.body}
                </div>
              </section>
            ))}
          </div>
        </div>
      </Container>
    </div>
  );
}